import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useState } from 'react';

import { Card, CardBody, Badge, Button, Input, PageHeader } from '@terra/ui';

import { api } from '../lib/api';

export function ProductSupportPage() {
  const qc = useQueryClient();
  const [activeTab, setActiveTab] = useState<'queries' | 'conversations'>('queries');

  const queries = useQuery({
    queryKey: ['vendor-product-queries'],
    queryFn: () => api.support.productQueries.listVendor(),
  });

  const conversations = useQuery({
    queryKey: ['vendor-product-conversations'],
    queryFn: () => api.support.productConversations.listVendor(),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Customer Support"
        description="Answer product queries and respond to direct customer messages."
      />
      <div className="flex gap-4 border-b border-slate-200 pb-2">
        <button
          onClick={() => setActiveTab('queries')}
          className={`font-semibold ${activeTab === 'queries' ? 'border-b-2 border-slate-900 text-slate-900' : 'text-slate-500'}`}
        >
          Product Queries ({queries.data?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('conversations')}
          className={`font-semibold ${activeTab === 'conversations' ? 'border-b-2 border-slate-900 text-slate-900' : 'text-slate-500'}`}
        >
          Conversations ({conversations.data?.length || 0})
        </button>
      </div>

      {activeTab === 'queries' && <VendorQueriesList queries={queries.data || []} />}
      {activeTab === 'conversations' && (
        <VendorConversationsList conversations={conversations.data || []} />
      )}
    </div>
  );
}

function VendorQueriesList({ queries }: { queries: any[] }) {
  const qc = useQueryClient();
  const [answeringId, setAnsweringId] = useState<string | null>(null);
  const [answer, setAnswer] = useState('');

  const submitAnswer = useMutation({
    mutationFn: (id: string) =>
      api.support.productQueries.answer(id, { answer, status: 'PUBLISHED' }),
    onSuccess: () => {
      setAnsweringId(null);
      setAnswer('');
      void qc.invalidateQueries({ queryKey: ['vendor-product-queries'] });
    },
  });

  if (queries.length === 0) return <div className="text-slate-500">No queries.</div>;

  return (
    <div className="space-y-4">
      {queries.map((q) => (
        <Card key={q.id}>
          <CardBody className="space-y-2">
            <div className="flex justify-between">
              <span className="font-semibold">{q.question}</span>
              <Badge>{q.status}</Badge>
            </div>
            {q.answer ? (
              <div className="rounded-md bg-slate-50 p-3 text-slate-700">{q.answer}</div>
            ) : (
              <div>
                {answeringId === q.id ? (
                  <div className="space-y-2">
                    <textarea
                      className="w-full rounded-md border p-2"
                      value={answer}
                      onChange={(e) => setAnswer(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button onClick={() => submitAnswer.mutate(q.id)}>Submit Answer</Button>
                      <Button variant="secondary" onClick={() => setAnsweringId(null)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button onClick={() => setAnsweringId(q.id)}>Answer</Button>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

function VendorConversationsList({ conversations }: { conversations: any[] }) {
  const qc = useQueryClient();
  const [activeConv, setActiveConv] = useState<any>(null);
  const [reply, setReply] = useState('');

  const detail = useQuery({
    queryKey: ['vendor-conv', activeConv?.id],
    queryFn: () => api.support.productConversations.get(activeConv!.id),
    enabled: !!activeConv,
  });

  const sendReply = useMutation({
    mutationFn: () => api.support.productConversations.reply(activeConv!.id, { body: reply }),
    onSuccess: () => {
      setReply('');
      void qc.invalidateQueries({ queryKey: ['vendor-conv', activeConv!.id] });
    },
  });

  if (activeConv) {
    return (
      <div className="space-y-4">
        <button onClick={() => setActiveConv(null)} className="text-blue-600">
          &larr; Back to conversations
        </button>
        <Card>
          <CardBody className="space-y-4">
            <h3 className="text-lg font-bold">{activeConv.subject}</h3>
            <div className="space-y-3">
              {detail.data?.messages.map((m: any) => (
                <div
                  key={m.id}
                  className={`rounded-lg p-3 ${m.senderId !== activeConv.buyerId ? 'ml-10 bg-blue-50' : 'mr-10 bg-slate-50'}`}
                >
                  <div className="mb-1 text-[11px] font-semibold text-slate-500">
                    {m.senderId === activeConv.buyerId ? 'Customer' : 'You'}
                  </div>
                  <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                  <div className="mt-1 text-right text-xs text-slate-400">
                    {format(new Date(m.createdAt), 'PP p')}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <Input
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type a reply..."
              />
              <Button
                onClick={() => sendReply.mutate()}
                disabled={sendReply.isPending || reply.trim().length === 0}
              >
                Send
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (conversations.length === 0) return <div className="text-slate-500">No conversations.</div>;

  return (
    <div className="space-y-3">
      {conversations.map((c) => (
        <Card
          key={c.id}
          className="cursor-pointer hover:bg-slate-50"
          onClick={() => setActiveConv(c)}
        >
          <CardBody className="flex items-center justify-between">
            <div>
              <div className="font-bold">{c.subject}</div>
              <div className="text-xs text-slate-500">{format(new Date(c.updatedAt), 'PP p')}</div>
            </div>
            <Badge>{c.status}</Badge>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}
